import { useState, useEffect } from "react";
import { Button, Label, TextInput, ToggleSwitch } from "flowbite-react";
import { HiFilter } from "react-icons/hi";
import { useTranslation } from "react-i18next";
import PaginatedSelect from "@/components/PaginatedSelect";
import { useGetClientGroupsQuery } from "@/api/clientGroupApi/clientGroup";

interface ClientFiltersProps {
  showInactive: boolean;
  onToggleInactive: (value: boolean) => void;
  onFilterChange: (filters: Record<string, any>) => void;
}

export default function ClientFilters({
  showInactive,
  onToggleInactive,
  onFilterChange,
}: ClientFiltersProps) {
  const { t } = useTranslation(["client", "common"]);
  const [showFilters, setShowFilters] = useState(false);

  const [usernameFilter, setUsernameFilter] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [phoneFilter, setPhoneFilter] = useState("");
  const [groupFilter, setGroupFilter] = useState<{ label: string; value: string } | null>(null);
  
  const [groupPage, setGroupPage] = useState(1);
  const [groupSearch, setGroupSearch] = useState("");

  const { data: clientGroupsData, isFetching: isGroupsFetching } = useGetClientGroupsQuery({
    pagination: { page: groupPage, pageSize: 15 },
    filters: { 
        is_active: true,
        group_name: groupSearch
    },
  });

  const groupFilterOptions = [
    { label: t("client:noGroup"), value: "null" },
    ...(clientGroupsData?.items || []).map((g: any) => ({
      label: g.group_name,
      value: String(g.client_group_id),
    })),
  ];

  const [debouncedUsername, setDebouncedUsername] = useState("");
  const [debouncedEmail, setDebouncedEmail] = useState("");
  const [debouncedPhone, setDebouncedPhone] = useState("");

  // Debounce effects
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedUsername(usernameFilter);
    }, 500);
    return () => clearTimeout(handler);
  }, [usernameFilter]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedEmail(emailFilter);
    }, 500);
    return () => clearTimeout(handler);
  }, [emailFilter]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedPhone(phoneFilter);
    }, 500);
    return () => clearTimeout(handler);
  }, [phoneFilter]);

  // Notify parent of filter changes
  useEffect(() => {
    const newFilters: Record<string, any> = {};

    if (debouncedUsername) {
      newFilters["client_username"] = debouncedUsername;
    }
    if (debouncedEmail) {
      newFilters["client_email"] = debouncedEmail;
    }
    if (debouncedPhone) {
      newFilters["client_phone"] = debouncedPhone;
    }
    if (groupFilter) {
      newFilters["client_group_id"] = groupFilter.value;
    }

    onFilterChange(newFilters);
  }, [debouncedUsername, debouncedEmail, debouncedPhone, groupFilter]);


  const handleClearFilters = () => {
    setUsernameFilter("");
    setEmailFilter("");
    setPhoneFilter("");
    setGroupFilter(null);
    setGroupSearch("");
    setGroupPage(1);
    onToggleInactive(false);
  };

  const handleToggleFilters = () => {
    if (showFilters) {
      handleClearFilters();
    }
    setShowFilters(!showFilters);
  };

  return (
    <div>
      <div className="mb-6 flex justify-start">
        <Button color="light" onClick={handleToggleFilters}>
          <HiFilter className="mr-2 h-5 w-5" />
          {showFilters ? t("common:hideFilters") : t("common:showFilters")}
        </Button>
      </div>

      {showFilters && (
        <div className="mb-6 rounded-lg bg-white p-4 shadow dark:bg-gray-800">
          <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
            {t("common:filters")}
          </h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-5">
            {/* Client Name */}
            <div>
              <Label htmlFor="client-username" className="mb-2 block">
                {t("client:clientName")}
              </Label>
              <TextInput
                id="client-username"
                className="mt-4"
                placeholder={t("client:searchClientName") || "Search name"}
                value={usernameFilter}
                onChange={(e) => setUsernameFilter(e.target.value)}
              />
            </div>

            {/* Client Email */}
            <div>
              <Label htmlFor="client-email" className="mb-2 block">
                {t("client:email")}
              </Label>
              <TextInput
                id="client-email"
                className="mt-4"
                placeholder={t("client:searchEmail") || "Search email"}
                value={emailFilter}
                onChange={(e) => setEmailFilter(e.target.value)}
              />
            </div>

            {/* Client Phone */}
            <div>
              <Label htmlFor="client-phone" className="mb-2 block">
                {t("client:phone")}
              </Label>
              <TextInput
                id="client-phone"
                className="mt-4"
                placeholder={t("client:searchPhone") || "Search phone"}
                value={phoneFilter}
                onChange={(e) => setPhoneFilter(e.target.value)}
              />
            </div>

            {/* Client Group */}
            <div>
              <Label htmlFor="client-group" className="mb-2 block">
                {t("client:clientGroup")}
              </Label>
              <PaginatedSelect
                id="client-group"
                options={groupFilterOptions}
                value={groupFilter}
                onChange={(selected) => setGroupFilter(selected as any)}
                onInputChange={(val) => { setGroupSearch(val); setGroupPage(1); }}
                isLoading={isGroupsFetching}
                placeholder={t("client:filterByGroup") || "Filter by group..."}
                page={groupPage}
                totalPages={clientGroupsData?.pagination?.totalPages || 1}
                onPageChange={setGroupPage}
                isClearable
                menuMaxHeight={350}
              />
            </div>

            {/* Inactive & Reset Actions */}
            <div className="flex flex-col justify-end gap-4 pb-1">
              <ToggleSwitch
                checked={showInactive}
                label={t("client:showInactive")}
                onChange={onToggleInactive}
              />
              <Button color="light" size="sm" onClick={handleClearFilters}>
                {t("common:reset")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
